import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, Download, Home } from 'lucide-react';
import { loginRequest } from '../auth/msalConfig';
import { useMe } from '../api/hooks';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { usePageTitleStore } from '../hooks/usePageTitle';

const TOP_NAV: { to: string; label: string }[] = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/customers', label: 'Clients' },
  { to: '/calendar',  label: 'Calendar' },
  { to: '/receipts',  label: 'Receipts' },
  { to: '/estimates', label: 'Estimates' },
  { to: '/documents', label: 'Documents' },
  { to: '/reports',   label: 'Reports' },
  { to: '/settings',  label: 'Settings' }
];

export function Header() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: me } = useMe(isAuthenticated);
  const { canInstall, promptInstall } = useInstallPrompt();
  const pageTitle = usePageTitleStore((s) => s.title);

  const onSignIn = () => {
    instance.loginRedirect(loginRequest).catch(console.error);
  };

  const onSignOut = () => {
    instance.logoutRedirect({
      account: accounts[0],
      postLogoutRedirectUri: '/'
    }).catch(console.error);
  };

  const isHome = location.pathname === '/dashboard';

  // Marketing is server-flagged (dark launch): only show it when the API says so.
  const navItems = me?.features?.marketingEnabled
    ? [
        ...TOP_NAV.slice(0, 6), // through Documents
        { to: '/marketing', label: 'Marketing' },
        ...TOP_NAV.slice(6)
      ]
    : TOP_NAV;

  const onBack = () => {
    // History API exposes the entry index in modern browsers; if we're at
    // the top of the stack (deep-link / fresh tab), go home instead.
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === 'number' && idx === 0) {
      navigate('/dashboard');
    } else {
      navigate(-1);
    }
  };

  return (
    <header
      className="sticky top-0 z-30 bg-white border-b border-slate-200"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {!isHome && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Go back"
              className="p-1 -ml-1 text-slate-600 hover:text-sky-600 min-h-[40px] min-w-[40px] flex items-center justify-center"
            >
              <ChevronLeft size={22} />
            </button>
          )}
          <Link
            to="/dashboard"
            aria-label="Home"
            className="flex items-center gap-1.5 text-sky-600 hover:text-sky-700 min-h-[40px]"
          >
            <Home size={18} />
            <span className="font-bold text-lg">ReceiptRaptor</span>
          </Link>
        </div>
        {pageTitle && (
          <div className="flex-1 text-center sm:hidden text-sm font-medium text-slate-700 truncate">
            {pageTitle}
          </div>
        )}
        <div className="flex items-center gap-3 text-sm">
          {me && (
            <span className="text-slate-700 font-medium hidden sm:inline truncate max-w-[10rem]">
              {me.businessName}
            </span>
          )}
          {canInstall && (
            <button
              type="button"
              onClick={() => promptInstall()}
              className="hidden sm:inline-flex items-center gap-1 text-slate-600 hover:text-sky-600"
              title="Install this app on your device"
            >
              <Download size={14} /> Install
            </button>
          )}
          {isAuthenticated ? (
            <button
              type="button"
              onClick={onSignOut}
              className="text-slate-600 hover:text-sky-600"
            >
              Sign out
            </button>
          ) : (
            <button
              type="button"
              onClick={onSignIn}
              className="bg-sky-600 text-white px-3 py-1.5 rounded hover:bg-sky-700"
            >
              Sign in
            </button>
          )}
        </div>
      </div>
      {isAuthenticated && (
        <nav
          aria-label="Primary"
          className="max-w-5xl mx-auto px-4 border-t border-slate-100"
        >
          <ul className="flex gap-1 overflow-x-auto whitespace-nowrap py-2 -mx-1 scrollbar-thin">
            {navItems.map((item) => (
              <li key={item.to} className="flex-shrink-0">
                <NavLink
                  to={item.to}
                  end={item.to === '/dashboard'}
                  className={({ isActive }) =>
                    `inline-flex items-center px-3 py-1.5 rounded-full text-sm transition-colors ${
                      isActive
                        ? 'bg-sky-100 text-sky-700 font-medium'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
}
